import { makeStyles } from "@mui/styles";
import { Button, Menu, MenuItem } from "@mui/material";
import { useContext, useState } from "react";
import { KeyboardArrowUpRounded } from "@mui/icons-material";
import { UserContext } from "../../../context/UserContext";
import { CourseStructureContext } from "../../../context/CourseStructureContext";
import { UserCtx, CourseStructureCtx } from "../../../interfaces";
import { MacsAuthenticator } from "../../../hooks/useMacsAuthenticator";
import { SortAndFilterStudentClasses } from "../../../Helpers";
import { _AuthType } from "../../../enums";

const useStyle = makeStyles((theme) => ({
  button: {
    maxWidth: "314px",
    height: "54px",
    padding: "8px 16px",
    fontSize: "1em",
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: "1em",
    overflow: "visible",
    backgroundColor: "transparent",
  },
  buttonClosed: {},
  buttonOpened: {
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    background: theme.palette.primary.light,
  },
  arrowDown: {
    height: "fit-content",
    transform: "rotate(0deg)",
    transition: "transform .2s linear",
  },
  arrowUp: {
    height: "fit-content",
    transform: "rotate(180deg)",
    transition: "transform .2s linear",
  },
  menuPaper: {
    overflow: "visible",
    backgroundColor: "transparent",
  },
  menuList: {
    transform: "translate(0px, 4px)",
    backgroundColor: "#FFF",
    boxShadow: "0px 8px 12px 0px rgba(0, 0, 0, 0.19)",
    width: "259px",
  },
  menuWrapper: {
    fontFamily: "Roboto, sans-serif",
    padding: "0 0.938rem",
  },
  menuName: {
    fontSize: 20,
    fontWeight: 700,
    padding: "0.25rem 1rem",
    margin: 0,
  },
  menuItem: {
    padding: "8px 12px",
    textWrap: "wrap",
  },
  menuItemSelected: {
    fontWeight: 700,
    backgroundColor: "#F6F6F6",
    color: theme.palette.text.primary,
  },
  menuItemIcon: {
    marginRight: "8px",

    fontSize: "1em",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "normal",
  },
}));

export const ClassSelector = () => {
  const classes = useStyle();
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const userCtx = useContext(UserContext) as UserCtx;
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const currentUser = userCtx.dbUser;
  const studentClasses = SortAndFilterStudentClasses(currentUser.class_student);
  const currentClass = studentClasses.find(
    (item) => item.id === Number(MacsAuthenticator.authHook.studentCurrentClassId)
  );

  const courseMetadata =
    courseStructureCtx.courseTreeStructure?.getCurrentCourseMetadata();
  const useDigitStudyTool = courseMetadata?.use_digit_study_tool ?? false;
  const disableSelector =
    studentClasses.length <= 1 ||
    (useDigitStudyTool === true && MacsAuthenticator.authHook.authType === _AuthType.LTI); // Selector should be disabled for useDigitStudyTool and LTI login

  const handleClick = (e: any) => {
    setMenuOpen(true);
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => {
    setMenuOpen(false);
    setAnchorEl(null);
  };

  const handleSelectClass = (newClassId: number) => {
    userCtx.handleChangeStudentClass(newClassId);
  };

  return (
    <>
      <Button
        id="class-selector"
        tabIndex={5}
        onClick={!disableSelector ? handleClick : undefined}
        className={`${classes.button} ${
          menuOpen ? classes.buttonOpened : classes.buttonClosed
        }`}
        disableRipple
        variant="outlined"
        color="primary"
        size="medium"
        sx={
          disableSelector
            ? {
                color: "#747474",
                borderColor: "#D0D0D0",
                cursor: "unset",
                "&:hover": {
                  color: "#747474",
                  borderColor: "#D0D0D0",
                  backgroundColor: "transparent",
                },
              }
            : undefined
        }>
        {currentClass
          ? `${currentClass.subject} ${currentClass.period_block}`
          : "Select Class"}
        {!disableSelector && (
          <KeyboardArrowUpRounded
            className={menuOpen ? classes.arrowDown : classes.arrowUp}
          />
        )}
      </Button>
      <Menu
        open={menuOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        classes={{
          root: classes.menuWrapper,
          paper: classes.menuPaper,
          list: classes.menuList,
        }}
        PaperProps={{
          elevation: 0,
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}>
        {studentClasses.map((studentClass) => (
          <MenuItem
            key={`class-selector-${studentClass.id}`}
            className={`${classes.menuItem} ${
              studentClass.id === currentClass?.id ? classes.menuItemSelected : ""
            }`}
            onClick={() => {
              handleClose();
              handleSelectClass(studentClass.id);
            }}>
            {`${studentClass.subject} ${studentClass.period_block}`}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
